import { useTranslation } from 'next-i18next';
import React from 'react';
import styled from 'styled-components';
import {
  AccordionMode,
  Accordion,
  AccordionItem,
} from '@hotelplan/components.common.accordion';
import { RadioButton } from '@hotelplan/components.common.buttons';
import { Icon } from '@hotelplan/components.common.icon';
import { sx2CssThemeFn } from '@hotelplan/util.theme.sxc';
import {
  ConfirmationModal,
  useConfirmation,
} from 'components/domain/confirmation';
import { useWishlistOverview } from 'fdr/components/candidate/fdr-wishlist/fdr-use-wishlist-overview';

interface IWishlistItemEditModalContentProps {
  onRemove: () => void;
  onMove: (wishlistId: string) => void;
}

const EditWishlistItemButton = styled.button(
  sx2CssThemeFn({
    display: 'flex',
    alignItems: 'center',
    borderRadius: 'default',
    border: '1px solid',
    borderColor: 'borderColorCard',
    width: '100%',
    color: 'black',
    px: 3,
    height: '50px',
    fontWeight: 'bold',
    boxShadow: 'cardShadow',
    '.icon': {
      ml: 'auto',
      color: 'primary',
    },
    letterSpacing: 'inherit',
    marginBottom: 3,
  })
);

const AccordionItemWrapper = styled(AccordionItem)(
  sx2CssThemeFn({
    '.chevron': {
      display: 'none',
    },
    '.accordionButtonWrap': {
      px: 0,
    },
    '.accordionButton': {
      border: 0,
      p: 0,
      fontSize: 2,
      overflow: 'hidden',
      mt: '-1px',
    },
    '.accordionContent': {
      p: 3,
      border: '1px solid',
      borderColor: 'borderColorCard',
      borderRadius: '0 0 4px 4px',
      maxHeight: ['200px', '350px'],
    },
    '.accordionButton.active': {
      '> div': {
        borderRadius: '4px 4px 0 0',
      },
    },
    '.accordion-icon-wrap': {
      display: 'flex',
      alignItems: 'center',
      borderRadius: 'default',
      border: '1px solid',
      borderColor: 'borderColorCard',
      width: '100%',
      color: 'black',
      px: 3,
      height: '50px',
      fontWeight: 'bold',
      boxShadow: 'cardShadow',
      '.icon': {
        ml: 'auto',
        color: 'primary',
      },
      letterSpacing: 'inherit',
    },
  })
);

const AccordionRadioButton = styled(RadioButton)(
  sx2CssThemeFn({
    bg: 'bgLightGrey',
    py: '12px',
    px: 3,
    borderRadius: 'default',
    mb: 2,
    '.active': {
      bg: 'primary',
    },
  })
);

const AccordionRadioButtonContent = styled.div(
  sx2CssThemeFn({
    display: 'flex',
    wordBreak: 'normal',
    overflow: 'hidden',
    flex: 1,
  })
);

const WishlistItemEditModalContent: React.FC<IWishlistItemEditModalContentProps> =
  ({ onMove, onRemove }) => {
    const [t] = useTranslation(['wishlist']);
    const { wishlists } = useWishlistOverview();

    const notActiveWishlists = wishlists?.filter(w => !w.active) || [];

    const [showConfirmation, confirmationProps] = useConfirmation(onRemove, {
      closeOnApply: true,
    });

    return (
      <>
        <EditWishlistItemButton onClick={showConfirmation}>
          {t('wishlist:deleteFromWishlist.button')}
          <Icon name={'delete'} />
        </EditWishlistItemButton>
        {notActiveWishlists.length > 0 && (
          <Accordion mode={AccordionMode.MULTIPLE}>
            <AccordionItemWrapper
              title={
                <div className="accordion-icon-wrap">
                  {t('wishlist:moveFromWishlist.button')}
                  <Icon name={'move-right'} />
                </div>
              }
              scrollable={true}
            >
              {notActiveWishlists.map(({ id, name }) => {
                return (
                  <AccordionRadioButton
                    key={`w-${id}`}
                    content={
                      <AccordionRadioButtonContent>
                        {name}
                      </AccordionRadioButtonContent>
                    }
                    onChange={() => onMove(id)}
                  />
                );
              })}
            </AccordionItemWrapper>
          </Accordion>
        )}
        <ConfirmationModal
          {...confirmationProps}
          title={t('wishlist:removeItem.confirmation.title')}
          question={t('wishlist:removeItem.confirmation.question')}
        />
      </>
    );
  };

export default WishlistItemEditModalContent;
